import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const SUPPORTED_LOCALES = ["en", "pl", "es", "fr"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "en";

export function isSupportedLocale(value: string | undefined): value is Locale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

// 从 pathname 提取语言前缀（如 /en/guides/xxx -> en）
export function getPathLocale(pathname: string): Locale | null {
  const seg = pathname.split("/").filter(Boolean)[0];
  return isSupportedLocale(seg) ? seg : null;
}

// 把某个路径（可能已带语言前缀）切换到指定语言
export function localizePath(pathname: string, lang: Locale): string {
  const current = getPathLocale(pathname);
  const base = current ? pathname.replace(`/${current}`, "") : pathname;
  const clean = base === "" ? "/" : base;
  return `/${lang}${clean === "/" ? "" : clean}`;
}

// 将 URL 中的语言同步到 i18n
export function useRouteLanguage() {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (isSupportedLocale(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);
}

// 给内部链接自动加语言前缀
export function useLocalizedLinks() {
  const { i18n } = useTranslation();
  const lang: Locale = isSupportedLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;

  const to = (path: string): string => {
    const hashIndex = path.indexOf("#");
    const hash = hashIndex >= 0 ? path.slice(hashIndex) : "";
    const pathname = hashIndex >= 0 ? path.slice(0, hashIndex) : path;

    const clean = pathname.startsWith("/") ? pathname.slice(1) : pathname;
    const base = clean === "" ? `/${lang}` : `/${lang}/${clean}`;
    return base + hash;
  };

  return { lang, to };
}